import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNoticeComponents } from '../hooks/useNoticeComponents';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import {
  useGetTSPsQuery,
  useUpdateNoticeComponentCapacityMutation,
} from '../services/edgeApi';
import { selectAllPoints, selectTsps } from '../map/slice/mapSelectors';
import { BooleanStatus } from '../helpers/BooleanStatus';
import { titleize } from '../utils/stringUtils';
import { PointBadge } from '../helpers/PointBadge';
import { useTheme } from '../helpers/hooks';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { CloseIcon, PlusIcon } from '@iconicicons/react';
import { CapacityComponent, NoticeComponent } from '@/types';
import toast from 'react-hot-toast';
import { CapacityComponentSearch } from '../capacityComponents/CapacityComponentSearch';

export type NoticeComponentTableProps = {};

export const NoticeComponentTable: React.FC<NoticeComponentTableProps> = () => {
  useGetTSPsQuery(undefined);
  const [theme] = useTheme();
  const tsps = useSelector(selectTsps);
  const points = useSelector(selectAllPoints);
  const { noticeComponents, isFetching, exhausted, next, refresh } =
    useNoticeComponents();
  const [updateNoticeComponentCapacity] =
    useUpdateNoticeComponentCapacityMutation();
  const [selectedNoticeComponent, setSelectedNoticeComponent] =
    React.useState<NoticeComponent | null>(null);

  const selectedComponentTsp = useMemo(
    () => (selectedNoticeComponent?.tsp ? [selectedNoticeComponent?.tsp] : []),
    [selectedNoticeComponent],
  );

  const removeCapacityComponent = async (component: NoticeComponent) => {
    if (confirm('Are you sure you want to remove the capacity component?')) {
      await updateNoticeComponentCapacity({
        componentId: component.id,
        capacityId: null,
      });
      refresh();
    }
  };
  const attachCapacityComponent = (component: NoticeComponent) => {
    setSelectedNoticeComponent(component);
  };

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Component</th>
            <th>TSP</th>
            <th>Status</th>
            <th>Type</th>
            <th>Direction</th>
            <th>Capacity Component</th>
            <th>Points</th>
            <th>Has Notice Activity</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(noticeComponents ?? {}).map((component) => {
            const tsp = tsps[component.tsp];
            return (
              <tr key={component.id}>
                <td>
                  <strong>#{component.id}</strong> {component.identifier}
                </td>
                <td>{tsp?.name}</td>
                <td>
                  <div className="d-flex gap-2">
                    <BooleanStatus
                      value={Boolean(component.capacity_component)}
                      trueText="Assigned"
                      falseText="Not Assigned"
                    />
                    <span>|</span>
                    <BooleanStatus
                      value={Boolean(component.is_mapped)}
                      trueText="Mapped"
                      falseText="Not Mapped"
                    />
                  </div>
                </td>
                <td>{titleize(component.capacity_type)}</td>
                <td>{titleize(component.flow_direction)}</td>
                <td>
                  {component.capacity_component_description ? (
                    <BadgeDisplay
                      theme={theme}
                      className="d-flex flex-column align-items-center justify-content-center"
                    >
                      {component.capacity_component_description}
                      <CloseIcon
                        role="button"
                        onClick={() => removeCapacityComponent(component)}
                      />
                    </BadgeDisplay>
                  ) : (
                    <BadgeDisplay
                      theme={theme}
                      role="button"
                      onClick={() => attachCapacityComponent(component)}
                    >
                      <PlusIcon />
                    </BadgeDisplay>
                  )}
                </td>
                <td>
                  {component.points.map(([pointId, _pointName]) => {
                    const point = points[pointId];
                    return (
                      <PointBadge
                        key={pointId.toString()}
                        point={point}
                        theme={theme}
                      />
                    );
                  })}
                </td>
                <td>
                  <BooleanStatus
                    value={component.has_recent_notices}
                    trueText="Yes"
                    falseText="No"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {isFetching && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '100px' }}
        >
          <LoadingIndicator />
        </div>
      )}
      {!exhausted && (
        <div className="d-flex justify-content-center">
          <InfiniteScrollObserver onScrollIn={next} />
        </div>
      )}
      <CapacityComponentSearch
        filters={{
          tsps: selectedComponentTsp,
        }}
        isSearching={Boolean(selectedNoticeComponent)}
        onSelect={async (capacityComponent: CapacityComponent) => {
          if (selectedNoticeComponent) {
            setSelectedNoticeComponent(null);
            await updateNoticeComponentCapacity({
              componentId: selectedNoticeComponent.id,
              capacityId: capacityComponent.id,
            });
            toast.success('Capacity component assigned successfully');
            refresh();
          }
        }}
        onClose={() => setSelectedNoticeComponent(null)}
      />
    </>
  );
};
